import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
export type { Tactic } from "@/types/entities";
import { toast } from "sonner";

export interface TacticFilters {
  focus_area_id?: string;
  cursor?: string;
  limit?: number;
}

export function useTactics(filters: TacticFilters = {}) {
  return useQuery({
    queryKey: ["tactics", filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/tactics", {
        params: { query: filters },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useCreateTactic() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateTacticRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/tactics", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tactics"] });
      toast.success("Strategic initiative created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateTactic(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UpdateTacticRequest"]) => {
      const { data, error } = await apiClient.PUT("/v1/tactics/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tactics"] });
      toast.success("Strategic initiative updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteTactic() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/tactics/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tactics"] });
      toast.success("Strategic initiative deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
